import * as React from 'react';
import {
  Box,
  HeadingText,
  InputStatusMessage,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import {
  EntityFormBanner,
  HintText,
  inputStatusIconLabelMap,
} from '@web-config-app/core-react-ui';
import { CompoundPrimitiveGroupFieldsetProps } from '../../types/components';

/**
 * CompoundPrimitiveGroupFieldset is used for 
 * type ObjectPrimitive = {
    children: Primitive | ArrayPrimitive
  }
  It is the same as CompoundPrimitiveGroup except renders a fieldset
  css prop is applied to the direct parent el of the rendered children
 */

export const CompoundPrimitiveGroupFieldset: React.FC<
  React.PropsWithChildren<CompoundPrimitiveGroupFieldsetProps>
> = ({
  hint,
  label,
  banner,
  children,
  childWrapperCss,
  inputStatus,
  statusMessage,
}) => {
  const { formatMessage } = useIntl();
  const showBanner = banner?.title || banner?.description;

  return (
    <Box css={{ marginBlockStart: '1.25rem', marginBlockEnd: '$two' }}>
      {showBanner && (
        <EntityFormBanner
          status={banner.status}
          title={banner.title}
          description={banner.description}
          css={{ marginBlockEnd: '$half' }}
        />
      )}
      <Box
        as="fieldset"
        css={{ width: '100%', borderWidth: '0px', padding: 0, margin: 0 }}
      >
        <HeadingText
          size="xs"
          as="legend"
          level="display"
          emphasis="subtle"
          css={{ padding: 0 }}
        >
          {label}
        </HeadingText>
        {hint && (
          <HintText size="sm" css={{ paddingBlockStart: '$quarter' }}>
            {hint}
          </HintText>
        )}
        {inputStatus && (
          <InputStatusMessage
            data-testid="status-message"
            inputStatus={inputStatus}
            statusIconLabel={formatMessage({
              id: inputStatusIconLabelMap[inputStatus],
            })}
            css={{ marginBlockStart: '$half' }}
          >
            {statusMessage}
          </InputStatusMessage>
        )}
        <Box
          css={{
            marginBlockStart: '$threeQuarters',
            ...childWrapperCss,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};
